/* iter55u — Static tables + tiny presentational bits extracted from
 * LandingPanel.jsx so the coordinator stays under 400 LOC.
 */
import React from 'react';
import { Check, AlertTriangle, X } from 'lucide-react';

// Field metadata used to render the Content tab in groups.
export const CONTENT_GROUPS = [
  {
    title: 'Top navigation',
    fields: [
      ['brand_name',         'Brand / wordmark'],
      ['top_email_signin',   'Email sign-in link'],
      ['top_google_signin',  'Google sign-in link'],
      ['top_open_app_label', '"Open The Loom" button'],
    ],
  },
  {
    title: 'Hero — text column',
    fields: [
      ['hero_eyebrow',         'Eyebrow (small pill above headline)'],
      ['hero_headline_part1',  'Headline — main text'],
      ['hero_headline_accent', 'Headline — accent text (last clause)'],
      ['hero_pre_headline',    'Pre-headline eyebrow (small pill above hero)'],
      ['hero_business_tagline','Tagline flag under hero (large italic block)'],
      ['hero_royalty_line',    'Royalty promise block under tagline', 'textarea'],
      ['hero_subline',         'Sub-headline paragraph (paragraph 2)',  'textarea'],
      ['hero_paragraph_3',     'Body paragraph 3',                       'textarea'],
      ['hero_paragraph_4_large', 'Body paragraph 4 — large / promise line', 'textarea'],
      ['hero_cta_primary',     'Primary CTA button label'],
      ['hero_cta_secondary',   'Secondary CTA button label'],
      ['hero_safety_note',     'Trust line under the buttons'],
    ],
  },
  {
    title: 'Hero — manuscript card (decorative)',
    fields: [
      ['hero_card_chapter', 'Chapter tag'],
      ['hero_card_status',  'Status pill'],
      ['hero_card_quote',   'Quote / sample line', 'textarea'],
      ['hero_card_mode_1',  'Mode chip 1'],
      ['hero_card_mode_2',  'Mode chip 2'],
      ['hero_card_mode_3',  'Mode chip 3'],
    ],
  },
  {
    title: 'The Loom card',
    fields: [
      ['pw_card_eyebrow', 'Eyebrow'],
      ['pw_card_title',   'Title'],
      ['pw_card_body',    'Body', 'textarea'],
      ['pw_card_cta',     'CTA label'],
    ],
  },
  {
    title: 'Book Store card',
    fields: [
      ['bs_card_eyebrow', 'Eyebrow'],
      ['bs_card_status',  'Status pill (e.g. "Coming soon")'],
      ['bs_card_title',   'Title'],
      ['bs_card_body',    'Body', 'textarea'],
      ['bs_card_cta',     'CTA label'],
    ],
  },
  {
    title: 'Mei intro video (login card video, right of sign-in)',
    fields: [
      ['mei_intro_title',       'Eyebrow label above the video'],
      ['mei_intro_video_url',   'Video file URL (e.g. /assets/mei-intro-video.mp4)', 'media-link-video'],
      ['mei_intro_poster_url',  'Poster image shown until play (e.g. /assets/mei-intro-poster.jpeg)', 'media-link-poster'],
    ],
  },
  {
    title: 'Footer',
    fields: [
      ['footer_text', 'Footer line'],
    ],
  },
  {
    title: 'How We Help Writers page (/how-we-help-writers)',
    fields: [
      ['hwh_hero_eyebrow',           'Hero eyebrow pill'],
      ['hwh_hero_title',             'Hero title (H1)'],
      ['hwh_hero_body',              'Hero sub-headline', 'textarea'],
      ['hwh_ai_offerings_headline',  'AI offerings — headline'],
      ['hwh_ai_offerings_body',      'AI offerings — body',    'textarea'],
      ['hwh_ai_offerings_bullets',   'AI offerings — bullets (one per line)', 'textarea'],
      ['hwh_bottom_cta_title',       'Bottom CTA — title'],
      ['hwh_bottom_cta_body',        'Bottom CTA — body',      'textarea'],
      ['hwh_testimonials',           'Testimonials (one per line · quote|author|book|avatar_url — avatar can be a URL, "@preset-1..6", or blank)', 'textarea'],
      ['hwh_invite_eyebrow',         'Top invite banner — eyebrow'],
      ['hwh_invite_headline',        'Top invite banner — headline (H2)'],
      ['hwh_invite_body',            'Top invite banner — body',   'textarea'],
      ['hwh_invite_button',          'Top invite banner — button label'],
    ],
  },
];

export const FONT_KEYS = [
  'system', 'inter', 'georgia', 'merriweather', 'playfair', 'ibm_plex',
];

export function Severity({ severity }) {
  if (severity === 'ok')   return <Check className="h-3.5 w-3.5 text-emerald-500" />;
  if (severity === 'warn') return <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />;
  return <X className="h-3.5 w-3.5 text-red-500" />;
}

export function ScoreRing({ score }) {
  const tone =
    score >= 80 ? 'text-emerald-500' :
    score >= 60 ? 'text-amber-500'   :
    'text-red-500';
  return (
    <div
      className={`flex h-16 w-16 items-center justify-center rounded-full border-4 ${tone}`}
      style={{ borderColor: 'currentColor' }}
    >
      <div className="text-center">
        <div className="font-mono-stat text-[18px] font-semibold leading-none">{score}</div>
        <div className="text-[9px] opacity-70">/ 100</div>
      </div>
    </div>
  );
}
